// Módulo-folha (sem import de React/UI) com o modelo de status/posse de uma
// ServiceSession — extraído de shared.tsx pra poder ser importado também por
// WhatsAppChatView.tsx sem reintroduzir o ciclo que fazia esse arquivo
// duplicar TERMINAL_STATUSES à mão (ver comentário removido lá).
import type { AgentType, AttendanceStatus } from '../../../services/attendances';
// User real do AuthContext/useAuth() — não confundir com types/auth.ts, um
// tipo legado usado só por mocks/authMock.ts e services/authService.ts
// (singular), não conectado ao AuthProvider em uso.
import type { User } from '../../../services/auth.service';

export const TERMINAL_STATUSES: AttendanceStatus[] = [
  'completed',
  'closed_inactivity',
  'closed_contact',
  'cancelled',
];

/** Espelha ServiceSession.OPEN_STATUSES (eleveia-api) — balde "Abertos" da
 * fila: não-terminal e fora do fluxo de NPS (awaiting_nps tem balde próprio,
 * "Aguardando avaliação" — já não é mais trabalho do operador). */
export const OPEN_STATUSES: AttendanceStatus[] = [
  'waiting',
  'active',
  'paused',
  'escalated',
];

/**
 * TERMINAL_STATUSES + 'awaiting_nps' — usado para desabilitar ações do
 * operador (responder, encerrar de novo, atribuir, registrar solicitação)
 * enquanto a sessão está no fluxo de captura de NPS (ServiceSession.
 * request_close() → AWAITING_NPS, ver CLAUDE.md do eleveia-api). A sessão
 * ainda NÃO está de fato encerrada nesse estado — por isso não faz parte
 * de TERMINAL_STATUSES em si (o badge "Encerrado" continua reservado só
 * aos status realmente terminais) — mas o atendimento já não é mais do
 * operador: o eleve-agent assumiu o contato só para perguntar a nota.
 */
export const NON_ACTIONABLE_STATUSES: AttendanceStatus[] = [
  ...TERMINAL_STATUSES,
  'awaiting_nps',
];

interface SessionLike {
  agent_user: number | null;
  agent_username: string | null;
  agent_type: AgentType;
  status: AttendanceStatus;
}

export interface SessionPermissions {
  /** Ninguém assumiu ainda (agent_user null) — cobre tanto "ainda com a IA"
   *  quanto "já escalada, mas sem dono". */
  isFree: boolean;
  /** O usuário logado é o dono (agent_user === currentUser.id). */
  isMine: boolean;
  isManager: boolean;
  /** coordinator — só-leitura, nunca assume nem gerencia sessão. */
  isReadOnlyRole: boolean;
  /** De fato encerrada (só isso mostra o badge "Encerrado"). */
  isClosed: boolean;
  /** Fora de ação do operador — inclui awaiting_nps. */
  isTerminal: boolean;
  /** "Assumir" — sessão livre, não fechada, role com permissão de agir. */
  canAssume: boolean;
  /**
   * Encerrar / Devolver p/ IA / Reatribuir / Devolver p/ fila. Sessão livre
   * (ninguém assumiu ainda) fica liberada pra qualquer role não-readonly —
   * mesma régua de "Assumir": não faz sentido exigir posse prévia de algo
   * que ninguém tem. Uma vez que alguém assumiu, só o dono ou um manager.
   */
  canManage: boolean;
  /** 'Você' | nome de quem assumiu | null (sessão livre). */
  ownerLabel: string | null;
}

export function getSessionPermissions(session: SessionLike, currentUser: User | null): SessionPermissions {
  const role = currentUser?.profile?.role;
  const isManager = role === 'manager';
  const isReadOnlyRole = role === 'coordinator';
  const isFree = session.agent_user == null;
  const isMine = !isFree && session.agent_user === currentUser?.id;
  const isClosed = TERMINAL_STATUSES.includes(session.status);
  const isTerminal = NON_ACTIONABLE_STATUSES.includes(session.status);

  return {
    isFree,
    isMine,
    isManager,
    isReadOnlyRole,
    isClosed,
    isTerminal,
    canAssume: !isClosed && isFree && !isReadOnlyRole,
    canManage: !isTerminal && !isReadOnlyRole && (isFree || isMine || isManager),
    ownerLabel: isFree ? null : (isMine ? 'Você' : session.agent_username),
  };
}
